/* eslint-disable */
import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PropTypes from 'prop-types';

/**
 * Error messages mapped by error category.
 */
const ERROR_MESSAGES = {
    network: 'Network unavailable. Check your connection.',
    timeout: 'Request timed out. Please try again.',
    server: 'Server error. Please try again later.',
    session: 'Session unavailable. Please log in again.'
};

/**
 * ErrorState component displays a categorized error message with a retry button.
 * Uses semantic HTML and ARIA attributes for accessibility.
 *
 * @param {Object} props
 * @param {'network' | 'timeout' | 'server' | 'session'} props.errorCategory - The category of error
 * @param {() => void} props.onRetry - Callback invoked when the retry button is clicked
 */
function ErrorState({ errorCategory, onRetry }) {
    const message = ERROR_MESSAGES[errorCategory] || ERROR_MESSAGES.server;

    return (
        <section role="alert" aria-live="assertive" aria-atomic="true" style={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 320,
                    textAlign: 'center',
                    p: { xs: 3, sm: 4, md: 5 },
                    border: '1px solid rgba(244, 63, 94, 0.12)',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(254, 242, 242, 0.4)', // Soft Rose backdrop tint
                    backdropFilter: 'blur(8px)'
                }}
            >
                {/* Custom warning badge */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        backgroundColor: '#FFF1F2',
                        color: 'error.main',
                        mb: 2.5,
                        boxShadow: '0 2px 8px rgba(244, 63, 94, 0.08)'
                    }}
                    aria-hidden="true"
                >
                    <ErrorOutlineIcon sx={{ fontSize: '26px' }} />
                </Box>

                <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                        fontSize: '17px',
                        fontWeight: 600,
                        color: 'text.primary',
                        mb: 1,
                        letterSpacing: '-0.01em'
                    }}
                >
                    Failed to load metrics
                </Typography>

                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '13.5px',
                        color: 'text.secondary',
                        mb: 3,
                        maxWidth: 380,
                        lineHeight: 1.5
                    }}
                >
                    {message}
                </Typography>

                <Button
                    variant="contained"
                    onClick={onRetry}
                    aria-label="Retry loading dashboard data"
                    sx={{
                        backgroundColor: '#0F172A', // Slate 900
                        color: '#FFFFFF',
                        fontSize: '13px',
                        fontWeight: 500,
                        px: 3,
                        py: 0.85,
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: '#1E293B', // Slate 800
                            boxShadow: 'none'
                        },
                        '&:focus-visible': {
                            outline: '2.5px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px'
                        }
                    }}
                >
                    Try again
                </Button>
            </Box>
        </section>
    );
}

ErrorState.propTypes = {
    errorCategory: PropTypes.oneOf(['network', 'timeout', 'server', 'session']).isRequired,
    onRetry: PropTypes.func.isRequired
};

export default ErrorState;
